import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import Input from "../../../common/components/form/input";

// Constants
const FIELD_START_DATE = "startDate";
const FIELD_END_DATE = "endDate";

const MarketQualityAttributeAssignmentInputGrid = ({
  selectedValues,
  allowEdit,
  applyStartDateToAll,
  applyEndDateToAll,
  globalStartDate,
  globalEndDate,
  globalNoEndDate,
  noEndDateFlags,
  onApplyToAllChange,
  onGlobalDateChange,
  onGlobalNoEndDateChange,
  onStartDateCellChange,
  onEndDateCellChange,
  onNoEndDateChange,
  onClearEndDate,
  onSave,
}) => {
  const { t } = useTranslation(["markets"]);

  // Helper function to create date column definition
  const createDateColumnDef = useCallback(
    (
      field,
      headerName,
      editableFn,
      onCellValueChangedFn,
      cellClassFn,
      editorParams
    ) => ({
      field,
      headerName,
      sortable: false,
      filter: false,
      flex: 1.5,
      cellDataType: "dateString",
      editable: editableFn,
      cellEditor: "agDateStringCellEditor",
      cellEditorParams: editorParams,
      valueGetter: (params) => {
        // Ensure we always return a valid date string or empty string
        const value = params.data?.[field];
        return value && typeof value === "string" ? value : "";
      },
      valueSetter: (params) => {
        // Set the value back to the data model
        if (params.data) {
          params.data[field] = params.newValue || "";
          return true;
        }
        return false;
      },
      onCellValueChanged: onCellValueChangedFn,
      cellClass: cellClassFn,
    }),
    []
  );

  const dateColumnDefs = useMemo(
    () => [
      {
        field: "qualityAttributeValue",
        headerName: t("QualityAttributeValue"),
        sortable: false,
        filter: false,
        flex: 2,
        cellClass: "table-bold",
      },
      createDateColumnDef(
        FIELD_START_DATE,
        t("StartDate"),
        (params) => allowEdit && !applyStartDateToAll,
        onStartDateCellChange,
        (params) =>
          allowEdit && !applyStartDateToAll ? "clickable-transparent" : "",
        {
          min: "2000-01-01",
          max: "2099-12-31",
        }
      ),
      createDateColumnDef(
        FIELD_END_DATE,
        t("EndDate"),
        (params) => {
          const qualityAttributeValueId = params.data.qualityAttributeValueId;
          const isNoEndDate = noEndDateFlags[qualityAttributeValueId] || false;
          return allowEdit && !applyEndDateToAll && !isNoEndDate;
        },
        onEndDateCellChange,
        (params) => {
          const qualityAttributeValueId = params.data.qualityAttributeValueId;
          const isNoEndDate = noEndDateFlags[qualityAttributeValueId] || false;
          return allowEdit && !applyEndDateToAll && !isNoEndDate
            ? "clickable-transparent"
            : "";
        },
        {
          min: (params) => params.data.startDate || undefined,
          max: "2099-12-31",
        }
      ),
      {
        field: "noEndDate",
        headerName: t("Actions"),
        sortable: false,
        filter: false,
        width: 160,
        cellClass: "ag-center-aligned-cell",
        cellRenderer: (params) => {
          const qualityAttributeValueId = params.data.qualityAttributeValueId;
          const endDate = params.data.endDate;

          // Only show button when endDate has a value
          if (!endDate || endDate.trim() === "") {
            return null;
          }

          return (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => onClearEndDate(qualityAttributeValueId)}
              disabled={!allowEdit}
              style={{
                borderColor: "var(--theme-table-hover-text)",
                color: "var(--theme-table-hover-text)",
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.backgroundColor =
                    "var(--theme-table-hover-text)";
                  e.currentTarget.style.color = "var(--theme-bg)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = "var(--theme-table-hover-text)";
              }}
            >
              {t("ClearEndDate")}
            </Button>
          );
        },
      },
    ],
    [
      t,
      allowEdit,
      applyStartDateToAll,
      applyEndDateToAll,
      noEndDateFlags,
      onStartDateCellChange,
      onEndDateCellChange,
      onClearEndDate,
      createDateColumnDef,
    ]
  );

  return (
    <div>
      <div className="mb-3">
        <div className="mb-2">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("SetStartDateForAll") || t("ApplyStartDateToAll")}
            checked={applyStartDateToAll}
            onChange={(e) =>
              onApplyToAllChange(FIELD_START_DATE, e.target.checked)
            }
            disabled={!allowEdit}
          />
        </div>
        {applyStartDateToAll && (
          <div className="mb-2">
            <Input
              type="date"
              value={globalStartDate}
              onChange={(e) =>
                onGlobalDateChange(FIELD_START_DATE, e.target.value)
              }
              label={t("StartDate")}
              disabled={!allowEdit}
            />
          </div>
        )}

        <div className="mb-2">
          <Form.Switch
            className="switch"
            type="switch"
            label={t("SetEndDateForAll") || t("ApplyEndDateToAll")}
            checked={applyEndDateToAll}
            onChange={(e) =>
              onApplyToAllChange(FIELD_END_DATE, e.target.checked)
            }
            disabled={!allowEdit}
          />
        </div>
        {applyEndDateToAll && (
          <div className="mb-2">
            <div className="row">
              <div className="col">
                <Input
                  type="date"
                  value={globalEndDate}
                  onChange={(e) =>
                    onGlobalDateChange(FIELD_END_DATE, e.target.value)
                  }
                  label={t("EndDate")}
                  disabled={!allowEdit || globalNoEndDate}
                  min={globalStartDate || undefined}
                />
              </div>
              <div className="col d-flex align-items-end">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={globalNoEndDate}
                    onChange={(e) => onGlobalNoEndDateChange(e.target.checked)}
                    disabled={!allowEdit}
                    id="globalNoEndDateCheckbox"
                  />
                  <label
                    className="form-check-label"
                    htmlFor="globalNoEndDateCheckbox"
                  >
                    {t("NoEndDate")}
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <ThemeableAgGrid
        className="table-bold"
        columnDefs={dateColumnDefs}
        rowData={selectedValues}
        gridOptions={{
          suppressCellFocus: false,
          singleClickEdit: true,
          stopEditingWhenCellsLoseFocus: true,
        }}
        getRowId={(params) => params.data.qualityAttributeValueId}
        domLayout="autoHeight"
        suppressPaginationPanel={true}
      />

      <div className="mt-3">
        {allowEdit && (
          <Button variant="primary" onClick={onSave}>
            {t("Save")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default MarketQualityAttributeAssignmentInputGrid;

